/* Axiom Supply — Custom Powdercoat picker.
 * <axiom-powdercoat> renders inside the product form (snippets/axiom-powdercoat.liquid).
 * Free-roam HSL color wheel + saturation/lightness sliders + hex + search, with the
 * actual selection LOCKED to the Prismatic Powders catalog: every wheel move live-ranks
 * the nearest catalog colors (CIE76 in Lab) and the selection snaps to a real powder.
 * The powdercoat itself is a separate cart line: the hidden "custom-powdercoat" service
 * product is AJAX-added (with Color/For/Lead Time props + a hidden _pcref pairing key)
 * before the native arm add re-fires; a cartUpdate reconciler keeps service qty == arm
 * qty and removes orphans. Express checkout is hidden while Custom is active (dynamic
 * checkout buttons drop line-item properties). Dataset:
 * assets/axiom-powdercoat-colors.json ("CODE|NAME|HEX|SLUG" lines; slug feeds the
 * "double-check on Prismatic Powders" link), lazy-fetched on first interest and shared
 * across instances via a window-level promise cache.
 */
if (!customElements.get('axiom-powdercoat')) {
  (() => {
    const dataCache = {};
    const SERVICE_HANDLE = 'custom-powdercoat';
    const LEAD_LABEL = 'Adds 5–10 days';
    const PP_BASE = 'https://www.prismaticpowders.com/shop/powder-coating-colors/';

    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

    /* Cart reconciliation: every powdercoat service line must mirror the quantity of
     * the arm line sharing its _pcref pairing key (orphans go to 0). Runs after any
     * cart update (drawer qty edits, removals), so pricing the service later stays
     * safe. Corrections re-render the drawer via the Section Rendering API. */
    let reconcileBound = false;
    let reconciling = false;
    function bindReconcile() {
      if (reconcileBound) return;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
      reconcileBound = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        reconcileCart();
      });
    }
    async function reconcileCart() {
      if (reconciling) return;
      reconciling = true;
      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const armQtyByRef = {};
        const serviceLines = [];
        for (const item of cart.items) {
          const ref = item.properties && item.properties._pcref;
          if (!ref) continue;
          if (item.handle === SERVICE_HANDLE) serviceLines.push(item);
          else armQtyByRef[ref] = (armQtyByRef[ref] || 0) + item.quantity;
        }
        const fixes = serviceLines.filter((s) => s.quantity !== (armQtyByRef[s.properties._pcref] || 0));
        for (let i = 0; i < fixes.length; i++) {
          const s = fixes[i];
          const body = { id: s.key, quantity: armQtyByRef[s.properties._pcref] || 0 };
          const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const last = i === fixes.length - 1;
          if (last && cartEl && typeof cartEl.getSectionsToRender === 'function' && typeof cartEl.renderContents === 'function') {
            body.sections = cartEl.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            const state = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            }).then((r) => r.json());
            try {
              cartEl.renderContents(state);
            } catch (e) {
              /* stale drawer is cosmetic; the cart itself is correct */
            }
          } else {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            if (last && document.querySelector('cart-items') && window.location.pathname.indexOf('/cart') === 0) {
              window.location.reload();
            }
          }
        }
      } catch (e) {
        /* transient cart errors: next cartUpdate retries */
      } finally {
        reconciling = false;
      }
    }

    function hslToRgb(h, s, l) {
      s /= 100;
      l /= 100;
      const k = (n) => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
    }

    function rgbToHsl(r, g, b) {
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const l = (max + min) / 2;
      let h = 0;
      let s = 0;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        else if (max === g) h = ((b - r) / d + 2) * 60;
        else h = ((r - g) / d + 4) * 60;
      }
      return [h, s * 100, l * 100];
    }

    function rgbToHex(r, g, b) {
      return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
    }

    function parseHex(str) {
      const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(str).trim());
      if (!m) return null;
      let hex = m[1];
      if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c);
      const n = parseInt(hex, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }

    function rgbToLab(r, g, b) {
      const lin = (v) => {
        v /= 255;
        return v > 0.04045 ? Math.pow((v + 0.055) / 1.055, 2.4) : v / 12.92;
      };
      const rl = lin(r);
      const gl = lin(g);
      const bl = lin(b);
      let x = (rl * 0.4124 + gl * 0.3576 + bl * 0.1805) / 0.95047;
      let y = rl * 0.2126 + gl * 0.7152 + bl * 0.0722;
      let z = (rl * 0.0193 + gl * 0.1192 + bl * 0.9505) / 1.08883;
      const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
      x = f(x);
      y = f(y);
      z = f(z);
      return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
    }

    function loadDataset(url) {
      if (!dataCache[url]) {
        dataCache[url] = fetch(url)
          .then((r) => {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then((json) => {
            const colors = json.colors.map((line) => {
              const [code, name, hex, slug] = line.split('|');
              const rgb = parseHex(hex);
              return { code, name, slug: slug || '', hex: '#' + hex, rgb, lab: rgbToLab(rgb[0], rgb[1], rgb[2]) };
            });
            return colors;
          })
          .catch((e) => {
            delete dataCache[url];
            throw e;
          });
      }
      return dataCache[url];
    }

    class AxiomPowdercoat extends HTMLElement {
      connectedCallback() {
        if (this.axiomBound) return;
        this.axiomBound = true;

        this.h = 20;
        this.s = 85;
        this.l = 50;
        this.colors = null;
        this.selected = null;
        this.matches = [];
        this.matchesScheduled = false;

        this.panel = this.querySelector('[data-axiom-pc-panel]');
        this.canvas = this.querySelector('[data-axiom-pc-wheel]');
        this.wheelWrap = this.querySelector('[data-axiom-pc-wheelwrap]');
        this.thumb = this.querySelector('[data-axiom-pc-thumb]');
        this.satInput = this.querySelector('[data-axiom-pc-sat]');
        this.lightInput = this.querySelector('[data-axiom-pc-light]');
        this.hexInput = this.querySelector('[data-axiom-pc-hex]');
        this.searchInput = this.querySelector('[data-axiom-pc-search]');
        this.resultsBox = this.querySelector('[data-axiom-pc-results]');
        this.matchesBox = this.querySelector('[data-axiom-pc-matches]');
        this.statusEl = this.querySelector('[data-axiom-pc-status]');
        this.retryBtn = this.querySelector('[data-axiom-pc-retry]');
        this.selSwatch = this.querySelector('[data-axiom-pc-selected-swatch]');
        this.selName = this.querySelector('[data-axiom-pc-selected-name]');
        this.selCode = this.querySelector('[data-axiom-pc-selected-code]');
        this.propColor = this.querySelector('[data-axiom-pc-prop-color]');
        this.propLead = this.querySelector('[data-axiom-pc-prop-lead]');
        this.propRef = this.querySelector('[data-axiom-pc-prop-ref]');
        this.ppLink = this.querySelector('[data-axiom-pc-pp-link]');
        this.form = this.closest('form');
        this.serviceVariant = parseInt(this.dataset.serviceVariant, 10) || 0;
        if (this.serviceVariant && this.form) {
          this.form.addEventListener('click', this.onFormClick.bind(this), true);
        }
        bindReconcile();

        const radios = this.querySelectorAll('[data-axiom-pc-mode]');
        radios.forEach((radio) => {
          radio.addEventListener('change', () => this.setMode(this.customMode()));
        });
        const customLabel = this.querySelector('[data-axiom-pc-custom-label]');
        if (customLabel) {
          const prefetch = () => {
            this.ensureData(false);
          };
          customLabel.addEventListener('pointerenter', prefetch, { once: true });
          customLabel.addEventListener('focusin', prefetch, { once: true });
        }
        if (this.retryBtn) this.retryBtn.addEventListener('click', () => this.ensureData(true));

        this.bindWheel();
        this.bindControls();
        this.bindSearch();

        if (this.customMode()) this.setMode(true);
        // Always normalize the hidden inputs on boot (clears any value the markup or
        // browser form-state restore left behind while they are disabled).
        this.syncProperties();
      }

      customMode() {
        const checked = this.querySelector('[data-axiom-pc-mode]:checked');
        return !!checked && checked.value === 'custom';
      }

      setMode(custom) {
        if (this.panel) {
          this.panel.setAttribute('data-open', custom ? 'true' : 'false');
          try {
            this.panel.inert = !custom;
          } catch (e) {
            /* inert unsupported: data-open CSS still hides it */
          }
        }
        if (this.form) this.form.classList.toggle('axiom-pc-hide-express', custom);
        if (custom) {
          this.ensureData(false);
          this.drawWheel();
          this.updateAll();
        }
        this.syncProperties();
      }

      ensureData(isRetry) {
        if (this.colors || this.loading) return;
        const url = this.dataset.colorsUrl;
        if (!url) return;
        this.loading = true;
        this.setStatus('Loading the color library…', false);
        loadDataset(url)
          .then((colors) => {
            this.colors = colors;
            this.loading = false;
            this.setStatus(colors.length.toLocaleString() + ' Prismatic Powders colors loaded. Move the wheel to explore.', false);
            this.scheduleMatches();
          })
          .catch(() => {
            this.loading = false;
            this.setStatus('The color library could not be loaded. Check your connection and try again.', true, true);
          });
        if (isRetry) this.setStatus('Loading the color library…', false);
      }

      setStatus(text, isError, showRetry) {
        if (!this.statusEl) return;
        this.statusEl.textContent = text;
        this.statusEl.classList.toggle('axiom-powdercoat__status--error', !!isError);
        if (this.retryBtn) this.retryBtn.hidden = !(isError && showRetry);
      }

      /* ---------- wheel ---------- */

      bindWheel() {
        if (!this.wheelWrap || !this.canvas) return;
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            this.drawWheel();
            this.positionThumb();
          });
          this.resizeObserver.observe(this.wheelWrap);
        }
        this.wheelWrap.addEventListener('pointerdown', (e) => {
          if (e.button !== 0 && e.pointerType === 'mouse') return;
          e.preventDefault();
          this.wheelWrap.setPointerCapture(e.pointerId);
          this.dragging = true;
          this.updateFromPointer(e);
        });
        this.wheelWrap.addEventListener('pointermove', (e) => {
          if (!this.dragging) return;
          e.preventDefault();
          this.updateFromPointer(e);
        });
        const stop = () => {
          this.dragging = false;
        };
        this.wheelWrap.addEventListener('pointerup', stop);
        this.wheelWrap.addEventListener('pointercancel', stop);

        if (this.thumb) {
          this.thumb.addEventListener('keydown', (e) => {
            const step = e.shiftKey ? 10 : 2;
            let handled = true;
            if (e.key === 'ArrowLeft') this.h = (this.h - step + 360) % 360;
            else if (e.key === 'ArrowRight') this.h = (this.h + step) % 360;
            else if (e.key === 'ArrowUp') this.s = clamp(this.s + step, 0, 100);
            else if (e.key === 'ArrowDown') this.s = clamp(this.s - step, 0, 100);
            else handled = false;
            if (handled) {
              e.preventDefault();
              this.pinned = null;
              this.updateAll();
            }
          });
        }
      }

      wheelMetrics() {
        const rect = this.wheelWrap.getBoundingClientRect();
        const size = Math.min(rect.width, rect.height);
        return { rect, size, cx: rect.width / 2, cy: rect.height / 2, radius: size / 2 };
      }

      updateFromPointer(e) {
        const m = this.wheelMetrics();
        const dx = e.clientX - m.rect.left - m.cx;
        const dy = e.clientY - m.rect.top - m.cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = (Math.atan2(dx, -dy) * 180) / Math.PI;
        this.h = (angle + 360) % 360;
        this.s = clamp((dist / m.radius) * 100, 0, 100);
        this.pinned = null;
        this.updateAll();
      }

      drawWheel() {
        if (!this.canvas || !this.wheelWrap) return;
        const m = this.wheelMetrics();
        if (m.size < 40) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const px = Math.round(m.size * dpr);
        if (this.canvas.width === px && this.wheelDrawn) return;
        this.canvas.width = px;
        this.canvas.height = px;
        const ctx = this.canvas.getContext('2d');
        const image = ctx.createImageData(px, px);
        const data = image.data;
        const c = px / 2;
        const R = c - 0.5;
        for (let y = 0; y < px; y++) {
          for (let x = 0; x < px; x++) {
            const dx = x - c;
            const dy = y - c;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const i = (y * px + x) * 4;
            if (dist > R + 1) continue;
            const hue = ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360;
            const sat = clamp((dist / R) * 100, 0, 100);
            const rgb = hslToRgb(hue, sat, 50);
            data[i] = rgb[0];
            data[i + 1] = rgb[1];
            data[i + 2] = rgb[2];
            data[i + 3] = Math.round(clamp(R + 1 - dist, 0, 1) * 255);
          }
        }
        ctx.putImageData(image, 0, 0);
        this.wheelDrawn = true;
      }

      positionThumb() {
        if (!this.thumb || !this.wheelWrap) return;
        const m = this.wheelMetrics();
        const rad = (this.h * Math.PI) / 180;
        const r = (this.s / 100) * m.radius;
        const x = m.cx + Math.sin(rad) * r;
        const y = m.cy - Math.cos(rad) * r;
        this.thumb.style.left = x + 'px';
        this.thumb.style.top = y + 'px';
        const rgb = hslToRgb(this.h, this.s, this.l);
        this.thumb.style.backgroundColor = rgbToHex(rgb[0], rgb[1], rgb[2]);
        this.thumb.setAttribute(
          'aria-valuetext',
          'Hue ' + Math.round(this.h) + ' degrees, saturation ' + Math.round(this.s) + ' percent'
        );
      }

      /* ---------- sliders + hex ---------- */

      bindControls() {
        if (this.satInput) {
          this.satInput.addEventListener('input', () => {
            this.s = clamp(parseFloat(this.satInput.value) || 0, 0, 100);
            this.pinned = null;
            this.updateAll();
          });
        }
        if (this.lightInput) {
          this.lightInput.addEventListener('input', () => {
            this.l = clamp(parseFloat(this.lightInput.value) || 0, 0, 100);
            this.pinned = null;
            this.updateAll();
          });
        }
        if (this.hexInput) {
          this.hexInput.addEventListener('input', () => {
            const rgb = parseHex(this.hexInput.value);
            if (!rgb) return;
            const hsl = rgbToHsl(rgb[0], rgb[1], rgb[2]);
            this.h = hsl[0];
            this.s = hsl[1];
            this.l = hsl[2];
            this.pinned = null;
            this.updateAll();
          });
          this.hexInput.addEventListener('blur', () => {
            const rgb = hslToRgb(this.h, this.s, this.l);
            this.hexInput.value = rgbToHex(rgb[0], rgb[1], rgb[2]);
          });
        }
      }

      /* ---------- search ---------- */

      bindSearch() {
        if (!this.searchInput || !this.resultsBox) return;
        const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const close = () => {
          this.resultsBox.hidden = true;
          this.searchInput.setAttribute('aria-expanded', 'false');
        };
        this.searchInput.addEventListener('input', () => {
          const q = norm(this.searchInput.value);
          if (!q || !this.colors) {
            close();
            return;
          }
          const hits = [];
          for (const c of this.colors) {
            if (norm(c.name).includes(q) || norm(c.code).includes(q)) {
              hits.push(c);
              if (hits.length >= 30) break;
            }
          }
          this.resultsBox.innerHTML = '';
          if (!hits.length) {
            const empty = document.createElement('p');
            empty.className = 'axiom-powdercoat__result-empty';
            empty.textContent = 'No colors match that search.';
            this.resultsBox.appendChild(empty);
          }
          for (const c of hits) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'axiom-powdercoat__result';
            btn.setAttribute('role', 'option');
            btn.innerHTML =
              '<span class="axiom-powdercoat__result-dot" style="background:' +
              c.hex +
              '"></span><span class="axiom-powdercoat__result-name"></span><span class="axiom-powdercoat__result-code"></span>';
            btn.querySelector('.axiom-powdercoat__result-name').textContent = c.name;
            btn.querySelector('.axiom-powdercoat__result-code').textContent = c.code;
            btn.addEventListener('click', () => {
              this.select(c, true);
              close();
              this.searchInput.value = '';
            });
            this.resultsBox.appendChild(btn);
          }
          this.resultsBox.hidden = false;
          this.searchInput.setAttribute('aria-expanded', 'true');
        });
        this.searchInput.addEventListener('focus', () => {
          this.ensureData(false);
        });
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') close();
          if (e.key === 'ArrowDown') {
            const first = this.resultsBox.querySelector('.axiom-powdercoat__result');
            if (first) {
              e.preventDefault();
              first.focus();
            }
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            const first = this.resultsBox.querySelector('.axiom-powdercoat__result');
            if (first) first.click();
          }
        });
        this.resultsBox.addEventListener('keydown', (e) => {
          const items = Array.from(this.resultsBox.querySelectorAll('.axiom-powdercoat__result'));
          const idx = items.indexOf(document.activeElement);
          if (e.key === 'ArrowDown' && idx > -1 && idx < items.length - 1) {
            e.preventDefault();
            items[idx + 1].focus();
          } else if (e.key === 'ArrowUp' && idx > 0) {
            e.preventDefault();
            items[idx - 1].focus();
          } else if (e.key === 'ArrowUp' && idx === 0) {
            e.preventDefault();
            this.searchInput.focus();
          } else if (e.key === 'Escape') {
            close();
            this.searchInput.focus();
          }
        });
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) close();
        });
      }

      /* ---------- matching + selection ---------- */

      scheduleMatches() {
        if (this.matchesScheduled) return;
        this.matchesScheduled = true;
        requestAnimationFrame(() => {
          this.matchesScheduled = false;
          this.computeMatches();
        });
      }

      computeMatches() {
        if (!this.colors || !this.matchesBox) return;
        const rgb = hslToRgb(this.h, this.s, this.l);
        const lab = rgbToLab(rgb[0], rgb[1], rgb[2]);
        const best = [];
        for (const c of this.colors) {
          const dL = lab[0] - c.lab[0];
          const da = lab[1] - c.lab[1];
          const db = lab[2] - c.lab[2];
          const d = dL * dL + da * da + db * db;
          if (best.length < 8) {
            best.push({ c, d });
            if (best.length === 8) best.sort((a, b) => a.d - b.d);
          } else if (d < best[7].d) {
            best[7] = { c, d };
            best.sort((a, b) => a.d - b.d);
          }
        }
        this.matches = best.map((b) => b.c);
        this.renderMatches();
        this.select(this.pinned || this.matches[0], false);
      }

      renderMatches() {
        if (!this.matchesBox) return;
        let tiles = this.matchesBox.querySelectorAll('.axiom-powdercoat__match');
        if (tiles.length !== this.matches.length) {
          this.matchesBox.innerHTML = '';
          for (let i = 0; i < this.matches.length; i++) {
            const tile = document.createElement('button');
            tile.type = 'button';
            tile.className = 'axiom-powdercoat__match';
            tile.addEventListener('click', () => {
              const idx = parseInt(tile.dataset.idx, 10);
              if (this.matches[idx]) this.select(this.matches[idx], true);
            });
            this.matchesBox.appendChild(tile);
          }
          tiles = this.matchesBox.querySelectorAll('.axiom-powdercoat__match');
        }
        tiles.forEach((tile, i) => {
          const c = this.matches[i];
          tile.dataset.idx = i;
          tile.style.backgroundColor = c.hex;
          tile.setAttribute('aria-label', c.name + ' ' + c.code);
          tile.title = c.name + ' ' + c.code;
          tile.classList.toggle('axiom-powdercoat__match--selected', this.selected === c);
        });
      }

      select(color, fromUser) {
        if (!color) return;
        this.selected = color;
        if (fromUser) {
          this.pinned = color;
          const hsl = rgbToHsl(color.rgb[0], color.rgb[1], color.rgb[2]);
          this.h = hsl[0];
          this.s = hsl[1];
          this.l = hsl[2];
          this.updateAll();
        }
        if (this.selSwatch) this.selSwatch.style.backgroundColor = color.hex;
        if (this.selName) this.selName.textContent = color.name;
        if (this.selCode) this.selCode.textContent = color.code;
        if (this.ppLink) {
          if (color.slug) {
            this.ppLink.href = PP_BASE + encodeURIComponent(color.code) + '/' + encodeURIComponent(color.slug);
            this.ppLink.hidden = false;
          } else {
            this.ppLink.hidden = true;
            this.ppLink.removeAttribute('href');
          }
        }
        if (this.matchesBox) {
          this.matchesBox.querySelectorAll('.axiom-powdercoat__match').forEach((tile) => {
            tile.classList.toggle(
              'axiom-powdercoat__match--selected',
              this.matches[parseInt(tile.dataset.idx, 10)] === this.selected
            );
          });
        }
        this.syncProperties();
      }

      syncProperties() {
        const active = this.customMode() && !!this.selected;
        const serviceMode = !!this.serviceVariant;
        if (this.propColor) {
          this.propColor.disabled = !active || serviceMode;
          this.propColor.value = this.propColor.disabled ? '' : this.selected.name + ' (' + this.selected.code + ')';
        }
        if (this.propLead) {
          this.propLead.disabled = !active || serviceMode;
          // Value lives here, not in the markup: express checkout ignores `disabled`
          // and would otherwise ship a preset value on standard-finish orders.
          this.propLead.value = this.propLead.disabled ? '' : LEAD_LABEL;
        }
        if (this.propRef) {
          this.propRef.disabled = !active || !serviceMode;
          this.propRef.value = this.propRef.disabled ? '' : this.serviceRef();
        }
      }

      serviceRef() {
        return (this.dataset.productId || 'p') + ':' + (this.selected ? this.selected.code : '');
      }

      formQuantity() {
        if (!this.form || !this.form.elements) return 1;
        const q = this.form.elements['quantity'];
        const v = q ? parseInt(q.value, 10) : 1;
        return v > 0 ? v : 1;
      }

      /* In service mode the first add click is intercepted (capture phase on the form):
       * the Custom Powdercoat service line is added via the AJAX API first, then the
       * native add re-fires so the theme's own flow posts the arm + renders the drawer
       * with both lines. If the service add fails, the arm add is blocked (never sell a
       * custom coat that isn't on the order). */
      onFormClick(event) {
        const trigger = event.target.closest('button[name="add"]');
        if (!trigger || trigger.disabled) return;
        if (this.serviceBypass) return;
        if (!this.customMode() || !this.selected) return;
        if (trigger.closest('modal-opener')) return;
        event.preventDefault();
        event.stopPropagation();
        this.addServiceItem(trigger);
      }

      addServiceItem(trigger) {
        if (this.serviceAddInFlight) return;
        this.serviceAddInFlight = true;
        const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const properties = { Color: this.selected.name + ' (' + this.selected.code + ')' };
        // Kit-scoped products (leveling kits): make the coverage explicit on the order line
        if (this.dataset.scopeLabel) properties['Applies To'] = this.dataset.scopeLabel;
        properties['For'] = this.dataset.productTitle || '';
        properties['Lead Time'] = LEAD_LABEL;
        properties['_pcref'] = this.serviceRef();
        fetch(base + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.serviceVariant, quantity: this.formQuantity(), properties }],
          }),
        })
          .then((r) => {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(() => {
            this.serviceAddInFlight = false;
            this.serviceBypass = true;
            trigger.click();
            setTimeout(() => {
              this.serviceBypass = false;
            }, 800);
          })
          .catch(() => {
            this.serviceAddInFlight = false;
            this.setStatus('Your custom color could not be attached. Please try again.', true);
          });
      }

      /* ---------- shared UI refresh ---------- */

      updateAll(skipMatches) {
        this.drawWheel();
        this.positionThumb();
        const rgb = hslToRgb(this.h, this.s, this.l);
        const hex = rgbToHex(rgb[0], rgb[1], rgb[2]);
        if (this.satInput) {
          this.satInput.value = Math.round(this.s);
          this.satInput.style.setProperty(
            '--pc-track',
            'linear-gradient(to right, ' +
              rgbToHex(...hslToRgb(this.h, 0, this.l)) +
              ', ' +
              rgbToHex(...hslToRgb(this.h, 100, this.l)) +
              ')'
          );
          this.satInput.style.setProperty('--pc-thumb', hex);
        }
        if (this.lightInput) {
          this.lightInput.value = Math.round(this.l);
          this.lightInput.style.setProperty(
            '--pc-track',
            'linear-gradient(to right, #000000, ' + rgbToHex(...hslToRgb(this.h, this.s, 50)) + ', #FFFFFF)'
          );
          this.lightInput.style.setProperty('--pc-thumb', hex);
        }
        if (this.hexInput && document.activeElement !== this.hexInput) this.hexInput.value = hex;
        if (!skipMatches) this.scheduleMatches();
      }
    }

    customElements.define('axiom-powdercoat', AxiomPowdercoat);
  })();
}
